import { type TeachPhoebeRecordedTraceEntry } from '@apps/web/components/phoebe_chat/user_question_model';
import { cn } from '@apps/web/lib/utils';

export type { TeachPhoebeRecordedTraceEntry };

function TeachPhoebeSproutIcon({ className }: { readonly className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
        >
            <path d="M5 21h14" />
            <path d="M14 9.536V7a4 4 0 0 1 4-4h1.5a.5.5 0 0 1 .5.5V5a4 4 0 0 1-4 4 4 4 0 0 0-4 4c0 2 1 3 1 5a5 5 0 0 1-1 3" />
            <path d="M4 9a5 5 0 0 1 8 4 5 5 0 0 1-8-4" />
        </svg>
    );
}

// Persistent Q&A trace shown after a training-mode ask-user submit replaces
// the old "Answer recorded" chip so the chat keeps what was taught.
export function TeachPhoebeRecordedTrace({
    entries,
    meta,
}: {
    readonly entries: ReadonlyArray<TeachPhoebeRecordedTraceEntry>;
    readonly meta?: string | null;
}) {
    return (
        <div
            data-testid="teach-phoebe-recorded-trace"
            className="bg-card border-border/60 w-full min-w-0 rounded-xl border p-3 shadow-sm"
        >
            <div className="flex items-center justify-between gap-2 pb-2">
                <p className="text-training-accent-strong flex items-center gap-1.5 text-xs font-semibold">
                    <TeachPhoebeSproutIcon className="text-training-accent h-3 w-3 shrink-0" />
                    Teach Phoebe
                </p>
                {meta ? (
                    <span className="text-muted-foreground text-[11px] font-medium tabular-nums">
                        {meta}
                    </span>
                ) : null}
            </div>
            <div className="divide-border/60 divide-y">
                {entries.map((entry) => (
                    <div
                        key={entry.questionId}
                        className="flex flex-col gap-0.5 py-1.5 first:pt-0 last:pb-0"
                    >
                        <p className="text-muted-foreground text-xs leading-snug">
                            {entry.questionDisplay}
                        </p>
                        <p
                            className={cn(
                                'text-sm leading-snug',
                                entry.skipped
                                    ? 'text-muted-foreground font-medium italic'
                                    : 'text-foreground font-semibold',
                            )}
                        >
                            {entry.skipped ? 'Skipped' : entry.answerDisplay}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}
